import React, { useState, useEffect } from "react";
import { useDropzone } from "react-dropzone";
import { FaArrowCircleUp, FaTrash, FaEdit, FaEye, FaTimes, FaCheck } from "react-icons/fa";
import { FiDownload } from "react-icons/fi";

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.bookwie.com';

const FileUploadAmenities = () => {
  const [banner1File, setBanner1File] = useState(null);
  const [amenityName, setAmenityName] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [amenities, setAmenities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingAmenity, setEditingAmenity] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Fetch the amenities list
  const fetchAmenities = async () => {
    setLoading(true);
    const token = localStorage.getItem("token");

    if (!token) {
      console.error("No token found");
      setUploadError("Authentication token is missing.");
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/admin/amenities`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = await response.json();
      console.log("Fetched amenities:", data);

      if (data.success) {
        setAmenities(data.data);
      } else {
        console.error("Error fetching amenities:", data.message);
        setUploadError("Failed to fetch amenities.");
      }
    } catch (error) {
      console.error("Error fetching amenities:", error);
      setUploadError("Network error while fetching amenities.");
    } finally {
      setLoading(false);
    }
  };

  // Handle file drop
  const onBanner1Drop = (acceptedFiles) => {
    const file = acceptedFiles[0];
    if (file) {
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setUploadError("File size must be less than 5MB");
        return;
      }
      
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setUploadError("Please select a valid image file");
        return;
      }
      
      setBanner1File(file);
      setUploadError(null);
    }
  };

  // Use dropzone hook
  const {
    getRootProps: getBanner1RootProps,
    getInputProps: getBanner1InputProps,
    isDragActive: isBanner1DragActive,
  } = useDropzone({
    onDrop: onBanner1Drop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp', '.svg']
    },
    multiple: false,
  });

  // Handle amenity name change
  const handleAmenityNameChange = (e) => {
    setAmenityName(e.target.value);
    if (uploadError && e.target.value.trim()) {
      setUploadError(null);
    }
  };

  // Reset form
  const resetForm = () => {
    setBanner1File(null);
    setAmenityName("");
    setUploadError(null);
    setUploadSuccess(null);
    setEditingAmenity(null);
  };

  // Handle file upload and submit form
  const handleUpload = async () => {
    // Validation
    if (!amenityName.trim()) {
      setUploadError("Please provide an amenity name.");
      return;
    }

    if (!banner1File && !editingAmenity) {
      setUploadError("Please select an image file.");
      return;
    }

    setUploading(true);
    setUploadError(null);
    setUploadSuccess(null);

    const token = localStorage.getItem("token");
    if (!token) {
      setUploadError("Authorization token is missing.");
      setUploading(false);
      return;
    }

    const formData = new FormData();
    formData.append("name", amenityName.trim());
    if (banner1File) {
      formData.append("image", banner1File);
    }

    try {
      const url = editingAmenity
        ? `${API_BASE_URL}/api/admin/amenities/${editingAmenity._id}`
        : `${API_BASE_URL}/api/admin/amenities`;

      const method = editingAmenity ? "PATCH" : "POST";

      const response = await fetch(url, {
        method: method,
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      const data = await response.json();
      console.log("Upload response:", data);
      console.log("Response status:", response.status);
      console.log("Response OK:", response.ok);

      if (data.success) {
        setUploadSuccess(
          editingAmenity
            ? "Amenity updated successfully!"
            : "Amenity uploaded successfully!"
        );
        resetForm();
        fetchAmenities();

        // Clear success message after 3 seconds
        setTimeout(() => setUploadSuccess(null), 3000);
      } else {
        const errorMessage = data.message || data.error || "Error processing amenity.";
        console.error("Backend error:", errorMessage);
        console.error("Full error response:", data);
        setUploadError(`Error: ${errorMessage}`);
      }
    } catch (error) {
      console.error("Error processing amenity:", error);
      setUploadError("Network error occurred. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  // Handle edit amenity
  const handleEdit = (amenity) => {
    setEditingAmenity(amenity);
    setAmenityName(amenity.name);
    setBanner1File(null);
    setUploadError(null);
    setUploadSuccess(null);
  };

  // Handle delete amenity
  const handleDelete = async (amenityId) => {
    const token = localStorage.getItem("token");
    if (!token) {
      setUploadError("Authorization token is missing.");
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/admin/amenities/${amenityId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = await response.json();
      
      if (data.success) {
        setUploadSuccess("Amenity deleted successfully!");
        fetchAmenities();
        setTimeout(() => setUploadSuccess(null), 3000);
      } else {
        setUploadError(data.message || "Error deleting amenity.");
      }
    } catch (error) {
      console.error("Error deleting amenity:", error);
      setUploadError("Network error occurred while deleting.");
    } finally {
      setDeleteConfirm(null);
    }
  };

  // Handle toggle status
  const handleToggleStatus = async (amenity) => {
    try {
      const response = await fetchWithAuth(
        `${API_BASE_URL}/api/admin/amenities/${amenity._id}/toggle-status`,
        {
          method: "PATCH",
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ active: !amenity.active }),
        }
      );

      const data = await response.json();
      
      if (data.success) {
        setUploadSuccess(`Amenity ${!amenity.active ? 'activated' : 'deactivated'} successfully!`);
        fetchAmenities();
        setTimeout(() => setUploadSuccess(null), 3000);
      } else {
        setUploadError(data.message || "Error updating amenity status.");
      }
    } catch (error) {
      console.error("Error updating amenity status:", error);
      // 401 errors are handled automatically by fetchWithAuth
      if (!error.message.includes('Session expired')) {
        setUploadError("Network error occurred while updating status.");
      }
    }
  };

  // View amenity in modal
  const handleView = (amenity) => {
    setEditingAmenity(amenity);
    setShowModal(true);
  };

  // Export amenities data
  const handleExport = () => {
    const csvContent = [
      ['Name', 'Status', 'Created At'],
      ...amenities.map(amenity => [
        amenity.name,
        amenity.active ? 'Active' : 'Inactive',
        new Date(amenity.createdAt).toLocaleDateString()
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `amenities_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Fetch amenities when component mounts
  useEffect(() => {
    fetchAmenities();
  }, []);

  return (
    <div className="space-y-5">
      <div className="bg-white rounded-3xl shadow-card p-6 mb-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-900">
            {editingAmenity ? 'Edit Amenity' : 'Add New Amenity'}
          </h2>
          <div className="flex space-x-2">
            <button
              onClick={handleExport}
              className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <FiDownload className="mr-2" />
              Export CSV
            </button>        
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Upload Section */}
          <div className="space-y-4">
            {/* File Upload */}
            <div className="border border-gray-300 border-dashed rounded-xl hover:border-blue-500 transition-colors">
              <div
                {...getBanner1RootProps()}
                className={`dropzone rounded-xl p-7 cursor-pointer transition-all
                  ${
                    isBanner1DragActive
                      ? "border-blue-500 bg-blue-50"
                      : "border-gray-300 bg-gray-50 hover:bg-gray-100"
                  }
                `}
              >
                <input {...getBanner1InputProps()} />

                <div className="flex flex-col items-center">
                  <div className="mb-4 flex justify-center">
                    <div className="flex h-16 w-16 items-center justify-center rounded-full bg-gray-200 text-gray-700">
                      <FaArrowCircleUp className="text-2xl" />
                    </div>
                  </div>

                  <h4 className="mb-3 font-semibold text-gray-800 text-lg">
                    {isBanner1DragActive
                      ? "Drop Files Here"
                      : "Drag & Drop Amenity Image"}
                  </h4>

                  <span className="text-center mb-3 block w-full max-w-xs text-sm text-gray-600">
                    Drag and drop your PNG, JPG, WebP, SVG images here or browse
                  </span>

                  <span className="font-medium text-blue-600 hover:text-blue-800 text-sm">
                    Browse File
                  </span>
                  
                  <div className="mt-2 text-xs text-gray-500">
                    Max file size: 5MB
                  </div>
                </div>
              </div>
            </div>

            {/* Amenity Name Input */}
            <div>
              <label htmlFor="amenityName" className="block text-sm font-medium text-gray-700 mb-2">
                Amenity Name *
              </label>
              <input
                id="amenityName"
                type="text"
                value={amenityName}
                onChange={handleAmenityNameChange}
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter amenity name..."
                maxLength={50}
              />
              <div className="mt-1 text-xs text-gray-500">
                {amenityName.length}/50 characters
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex space-x-3">
              <button
                onClick={handleUpload}
                disabled={uploading}
                className={`flex-1 py-3 px-4 rounded-lg font-medium transition-colors ${
                  uploading
                    ? "bg-gray-400 text-white cursor-not-allowed"
                    : "bg-blue-600 text-white hover:bg-blue-700"
                }`}
              >
                {uploading ? "Processing..." : editingAmenity ? "Update Amenity" : "Upload Amenity"}
              </button>
              
              {editingAmenity && (
                <button
                  onClick={resetForm}
                  className="px-4 py-3 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
              )}
            </div>

            {/* Status Messages */}
            {uploadSuccess && (
              <div className="p-3 bg-green-100 border border-green-400 text-green-700 rounded-lg">
                {uploadSuccess}
              </div>
            )}
            {uploadError && (
              <div className="p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                {uploadError}
              </div>
            )}
          </div>

          {/* Preview Section */}
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Preview</h3>
            {banner1File ? (
              <div className="border border-gray-300 rounded-lg p-4">
                <h5 className="font-medium text-gray-800 mb-3">Selected Image:</h5>
                <img
                  src={URL.createObjectURL(banner1File)}
                  alt="Preview"
                  className="w-full max-w-xs rounded-lg shadow-sm"
                />
                <div className="mt-2 text-sm text-gray-600">
                  Size: {(banner1File.size / 1024 / 1024).toFixed(2)} MB
                </div>
              </div>
            ) : editingAmenity && editingAmenity.image ? (
              <div className="border border-gray-300 rounded-lg p-4">
                <h5 className="font-medium text-gray-800 mb-3">Current Image:</h5>
                <img
                  src={editingAmenity.image}
                  alt={editingAmenity.name}
                  className="w-full max-w-xs rounded-lg shadow-sm"
                />
              </div>
            ) : (
              <div className="border border-gray-300 border-dashed rounded-lg p-8 text-center text-gray-500">
                No image selected
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Amenities List */}
      <div className="bg-white rounded-3xl shadow-card p-6">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-semibold text-gray-800">
            All Amenities ({amenities.length})
          </h3>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            <span className="ml-2 text-gray-600">Loading amenities...</span>
          </div>
        ) : amenities.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <p className="text-lg">No amenities found.</p>
            <p className="text-sm">Add your first amenity using the form above.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {amenities.map((amenity) => (
              <div
                key={amenity._id}
                className="bg-gray-50 rounded-lg p-4 border border-gray-200 hover:shadow-md transition-shadow"
              >
                <div className="flex justify-between items-start mb-3">
                  <h5 className="font-semibold text-gray-800 truncate flex-1 mr-2">
                    {amenity.name}
                  </h5>
                  <div className="flex items-center space-x-1">
                    <button
                      onClick={() => handleToggleStatus(amenity)}
                      className={`p-1 rounded ${
                        amenity.active ? 'text-green-600 hover:bg-green-100' : 'text-gray-400 hover:bg-gray-200'
                      }`}
                      title={amenity.active ? 'Active' : 'Inactive'}
                    >
                      <FaCheck className="text-sm" />
                    </button>
                  </div>
                </div>
                
                {amenity.image && (
                  <img
                    src={amenity.image}
                    alt={amenity.name}
                    className="w-full h-32 object-cover rounded-lg mb-3"
                  />
                )}
                
                <div className="flex justify-between items-center">
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    amenity.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                  }`}>
                    {amenity.active ? 'Active' : 'Inactive'}
                  </span>
                  
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleView(amenity)}
                      className="p-2 text-blue-600 hover:bg-blue-100 rounded transition-colors"
                      title="View"
                    >
                      <FaEye className="text-sm" />
                    </button>
                    <button
                      onClick={() => handleEdit(amenity)}
                      className="p-2 text-yellow-600 hover:bg-yellow-100 rounded transition-colors"
                      title="Edit"
                    >
                      <FaEdit className="text-sm" />
                    </button>
                    <button
                      onClick={() => setDeleteConfirm(amenity)}
                      className="p-2 text-red-600 hover:bg-red-100 rounded transition-colors"
                      title="Delete"
                    >
                      <FaTrash className="text-sm" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* View Modal */}
      {showModal && editingAmenity && (
        <div className="fixed inset-0 bg-half-transparent flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-xl max-w-lg w-full">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold">Amenity Details</h3>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <FaTimes className="text-xl" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-600">Name:</label>
                  <p className="text-lg">{editingAmenity.name}</p>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-600">Status:</label>
                  <span className={`ml-2 px-2 py-1 text-xs rounded-full ${
                    editingAmenity.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                  }`}>
                    {editingAmenity.active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                
                {editingAmenity.image && (
                  <div>
                    <label className="text-sm font-medium text-gray-600">Image:</label>
                    <img
                      src={editingAmenity.image}
                      alt={editingAmenity.name}
                      className="mt-2 w-full rounded-lg"
                    />
                  </div>
                )}
                
                <div>
                  <label className="text-sm font-medium text-gray-600">Created:</label>
                  <p className="text-sm text-gray-500">
                    {new Date(editingAmenity.createdAt).toLocaleDateString()}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-half-transparent flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-xl max-w-md w-full">
            <div className="p-6">
              <h3 className="text-lg font-bold mb-4">Confirm Deletion</h3>
              <p className="text-gray-600 mb-6">
                Are you sure you want to delete "{deleteConfirm.name}"? This action cannot be undone.
              </p>
              <div className="flex space-x-3">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="flex-1 px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDelete(deleteConfirm._id)}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FileUploadAmenities;